import React, { createContext, useContext } from 'react';
import { CountryCode } from 'shared/constants/countries';
import { hasInternalInvoicing } from 'shared/constants/invoices/regions';

import { useAccountingProviderState } from '@/hooks/useAccountingProvider';
import { useRegion } from '@/hooks/useRegion';

export type ContextValue = {
    isInvoicingAvailable: boolean;
    isInvoiceSystemEnabled: boolean;
    isInternalInvoicingRegion: boolean;
    isAccountingProviderAvailable: boolean;
    isInvoiceCapEnabled: boolean;
};

export type Props = {
    children: React.ReactNode;
};

const InvoicingSystemContext = createContext<ContextValue>({
    isInvoicingAvailable: false,
    isInvoiceSystemEnabled: false,
    isInternalInvoicingRegion: false,
    isAccountingProviderAvailable: false,
    isInvoiceCapEnabled: false,
});

const InvoicingSystemContextProvider = ({ children }: Props) => {
    const region = useRegion();
    const { accountingProvider, isLoaded } = useAccountingProviderState();

    const isUSRegion = region?.code === CountryCode.unitedStates;
    const isInternalInvoicingRegion = hasInternalInvoicing(region?.code);
    const usesInternalInvoicing =
        isInternalInvoicingRegion && isLoaded && !accountingProvider;

    return (
        <InvoicingSystemContext.Provider
            value={{
                // Web Invoicing is gated by region only. The US is
                // invoicing-only on mobile, so it must stay hidden on web
                // regardless of any accounting provider record — a stale or
                // failed Xero/QBO connect must not unlock it. Mobile US
                // invoicing is separate
                // (mobile/src/hooks/useIsInvoiceSystemEnabled.tsx).
                isInvoicingAvailable: !isUSRegion && !usesInternalInvoicing,
                isInvoiceSystemEnabled: isUSRegion,
                isInternalInvoicingRegion,
                isAccountingProviderAvailable: !isUSRegion,
                isInvoiceCapEnabled: isInternalInvoicingRegion,
            }}
        >
            {children}
        </InvoicingSystemContext.Provider>
    );
};

function useInvoicingSystemContext() {
    const context = useContext(InvoicingSystemContext);

    return context;
}

export { InvoicingSystemContextProvider, useInvoicingSystemContext };
